<script lang="ts" context="module">
    import { fade, scale } from 'svelte/transition';
    import { quadInOut, quadOut, quartInOut } from 'svelte/easing';
    import { createDialog } from '@melt-ui/svelte';
    import type { CreateDialogProps } from '@melt-ui/svelte';

    export function overlayIn(node: Element) {
        return fade(node, {
            duration: 300,
            easing: quadOut,
        });
    }

    export function overlayOut(node: Element) {
        return fade(node, {
            duration: 500,
            easing: quadInOut,
        });
    }

    export function modalIn(node: Element) {
        return scale(node, {
            delay: 0,
            duration: 300,
            easing: quadOut,
            start: 0.95,
            opacity: 0,
        });
    }

    export function modalOut(node: Element) {
        return scale(node, {
            delay: 0,
            duration: 300,
            easing: quartInOut,
            start: 0.95,
            opacity: 0,
        });
    }

    export function createModal(
        config: CreateDialogProps,
    ): ReturnType<typeof createDialog> {
        return createDialog({
            portal: '#svelte-portal',
            escapeBehavior: 'close',
            closeOnOutsideClick: true,
            forceVisible: true,
            defaultOpen: false,
            ...config,
        });
    }
</script>

<script lang="ts">
    import { cx } from '$lib/utils/cx.js';
    import { melt } from '@melt-ui/svelte';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';

    export let zIndex: number | 'current' | 'inc' = 1050;
    export let contentClass = '';
    export let defaultContentClass = cx(
        'w-full overflow-hidden bg-white shadow-xl transition-all',
        'rounded-t-none rounded-b-2xl',
        't:rounded-t-2xl t:rounded-b-2xl',
    );

    export let dialogClass = '';
    export let defaultDialogClass =
        'flex min-h-full items-center justify-center text-center t:pt-14 pb-28 z-[--z-index]';

    export let backdropClass = '';
    export let defaultBackdropClass = 'fixed inset-0 bg-black/60';

    export let modal: ReturnType<typeof createDialog>;
    const {
        elements: { portalled, overlay, content },
        states: { open },
    } = modal;
</script>

<StackZIndex {zIndex} let:zIndex>
    <div
        use:melt={$portalled}
        class="relative z-[--z-index]"
        style={`--z-index: ${zIndex};`}
    >
        {#if $open}
            <div
                use:melt={$overlay}
                class={cx(defaultBackdropClass, backdropClass)}
                in:overlayIn
                out:overlayOut
            />
            <div class="fixed inset-0 overflow-y-auto overscroll-contain">
                <div class={cx(defaultDialogClass, dialogClass)}>
                    <div
                        use:melt={$content}
                        in:modalIn
                        out:modalOut
                        class={cx(defaultContentClass, contentClass)}
                    >
                        <slot />
                    </div>
                </div>
            </div>
        {/if}
    </div>
</StackZIndex>
